import { CommonModule } from '@angular/common';
import { injectUrlViewState } from '../../../utils/url-view-state.util';

type PromotionFilter = 'all' | 'active' | 'in-active' | 'archived';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { AddPromotionModalComponent } from './modal/add-promotion-modal/add-promotion-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder } from '@angular/forms';
import { PromotionService } from '../../../services/promotion.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { MenuService } from '../../../services/menu.service';

@Component({
  selector: 'app-promotions',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './promotions.component.html',
  styleUrl: './promotions.component.css',
})
export class PromotionsComponent {
  promotionService = inject(PromotionService);
  menuService = inject(MenuService);
  ngxSpinnerService = inject(NgxSpinnerService);
  promotions: any = [];
  originalPromotions: any = [];
  menus: any = [];
  /** The All / Active / Inactive / Archived filter, kept in the URL
   * (?status) so a browser reload reopens the same list. */
  statusFilter: PromotionFilter = 'all';
  private urlState = injectUrlViewState();

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.statusFilter = this.urlState.pick<PromotionFilter>(
      'status',
      ['all', 'active', 'in-active', 'archived'],
      'all',
    );
    this.getAllMenus();
  }

  filterPromotion(status: PromotionFilter) {
    this.statusFilter = status;
    this.urlState.set('status', status, 'all');
    if (status == 'all') {
      this.promotions = this.originalPromotions;
    } else if (status == 'active') {
      this.promotions = this.originalPromotions.filter((f: any) => f.is_active);
    } else if (status == 'in-active') {
      this.promotions = this.originalPromotions.filter((f: any) => !f.is_active && !f.is_deleted);
    } else if (status == 'archived') {
      this.promotions = this.originalPromotions.filter((f: any) => f.is_deleted);
    }
    this.mergePromotionsWithMenu(this.promotions);
  }

  // Opens a modal to add or edit a promotion
  addNewPromotion(promotion?: any) {
    const dialogRef = this.dialog.open(AddPromotionModalComponent, {
      data: {
        promotion: promotion ? promotion : null,
        allPromotions: this.promotions,
        menus: this.menus,
      },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (promotion) {
          this.editPromotion(result, promotion.promotion_id); // Edit existing promotion
        } else {
          this.saveNewPromotion(result); // Save new promotion
        }
      }
    });
  }

  saveNewPromotion(promotion: any) {
    this.ngxSpinnerService.show();
    this.promotionService.createNewPromotion(promotion).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success(response.data.message, 'Success');
          this.getPromotions();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  editPromotion(promotion: any, id: any) {
    this.ngxSpinnerService.show();
    this.promotionService.editPromotion(promotion, id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success(response.data.message, 'Success');
          this.getPromotions();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  getPromotions() {
    this.ngxSpinnerService.show();
    this.promotionService.getAllPromotions().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.originalPromotions = response.data;
        // Re-applies the chosen filter (All, or the one the URL names).
        this.filterPromotion(this.statusFilter);
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  mergePromotionsWithMenu(promotions: any) {
    this.promotions.forEach((promotion: any) => {
      if (promotion.scope_options && promotion.scope_options.length > 0) {
        let temp_options: any = [];
        promotion.scope_options.forEach((option: any) => {
          let findMenu = this.menus.find((menu: any) => menu.id === option);
          if (findMenu) {
            temp_options.push({ id: findMenu.id, name: findMenu.name });
          }
        });
        if (temp_options.length > 0) {
          promotion.scope_options = temp_options;
        }
      }
    });
  }

  getAllMenus() {
    this.ngxSpinnerService.show();
    this.menuService.getMenus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        response.data.menus.forEach((menu: any) => {
          this.menus.push({ id: menu.menu_id, name: menu.name, promotion: menu.promotion });
        });
        this.ngxSpinnerService.hide();
        this.getPromotions();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
